import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import type { ProjectListItem } from '@/entities/project';
import { useTranslation } from 'react-i18next';
import { ProjectPickerPopover } from '../ProjectPickerPopover';
import { ProjectPickerItem } from '../SaveToProjectPopover/ProjectPickerItem';

// Mirrors what the create-brief route requires. Gated at the pick rather than at
// the persist call because generation runs a full agent pipeline first, so an
// ungated pick surfaces the 403 minutes after the mistake.
const REQUIRED_PERMISSIONS = [PROJECT_PERMISSIONS.ProjectEditDetails];

interface StartBriefPopoverProps {
  children: React.ReactNode;
  workspaceId: string | null | undefined;
  /** Called when the user picks a project to start a brief in. */
  onProjectSelected: (project: ProjectListItem) => void;
  /** Called when the popover opens or closes, after the picker has handled it. */
  onOpenChange?: (open: boolean) => void;
  /**
   * Holds the picker in its loading state on top of its own fetch. Lets a
   * consumer that prepares something on open (minting an artifact to pre-attach,
   * say) keep the list unselectable until that work settles.
   */
  additionalLoadingCheck?: boolean;
}

/**
 * "Start Brief" project picker: a popover that lists the workspace's projects
 * and emits the chosen one via `onProjectSelected`.
 *
 * Picker-ONLY by design. The intake → progress orchestration is NOT here — it
 * lives in the consumer (a page or the config-layer composition root) via the
 * headless `useStartBriefFlow` hook, because the brief modals live in
 * `@/features/brief-generation` and `shared` may not import features. Keeping
 * this component picker-only lets each consumer wire the concrete modals with
 * their real payload type.
 */
export function StartBriefPopover({
  children,
  workspaceId,
  onProjectSelected,
  onOpenChange,
  additionalLoadingCheck,
}: StartBriefPopoverProps) {
  const { t } = useTranslation('common');

  return (
    <ProjectPickerPopover
      workspaceId={workspaceId}
      searchTitle={t('ui.startBriefPopover.selectAProjectToCreate')}
      onOpenChange={onOpenChange}
      additionalLoadingCheck={additionalLoadingCheck}
      includePermissions
      renderProject={(project, picker) => (
        <ProjectPickerItem
          key={project.id}
          project={project}
          requiredPermissions={REQUIRED_PERMISSIONS}
          deniedMessage={t('ui.startBriefPopover.youDonTHavePermission')}
          onClick={() => {
            onProjectSelected(project);
            picker.setOpen(false);
          }}
          role="button"
        />
      )}
    >
      {children}
    </ProjectPickerPopover>
  );
}
